import Request from "./Request.ts";
import Controller from "./Controller.ts";
import {
  KLARITICS_STATUS_KEY,
  APXOR_STATUS_KEY,
  KLARITICS_STATUS_SUCCESS,
  // DEFAULT_CONFIG,
  DETAILS_KEY,
  SDK_VERSION
} from "./constants.ts";
import Logger from "./Logger.ts";
import { isFunction } from "./utils.ts";
import type { Chunk } from "./types/types.ts";

const CHUNKS_API = "/v1/api/projects/<project-id>/chunks";
const CONFIG_API = "/v1/api/projects/<project-id>/config";
const SDK_ENABLED_API =
  "/v1/api/projects/<project-id>/enabled-status?platform=web&sdk_version=" +
  SDK_VERSION;
const POST_USER_INFO_API = "/v1/api/user?appId=<project-id>";
const RECORD_API = "api/session-replay?appId=<project-id>";

// const API_ROOT = DEFAULT_CONFIG.api_root;

const _makeAPI = (api: string, siteId: string, host: string): string =>
  host + api.replace("<project-id>", siteId);

type ResolveCallback = (response: unknown) => void;
type RejectCallback = (response?: unknown) => void;

export default class API {
  private request: Request;
  private configAPI: string;
  private chunksAPI: string;
  private sdkEnabledStatusAPI: string;
  private userInfoAPI: string;
  private recordAPI: string;

  constructor(siteId: string, win: Window & typeof globalThis, host: string) {
    this.request = new Request(win);
    this.configAPI = _makeAPI(CONFIG_API, siteId, host);
    this.chunksAPI = _makeAPI(CHUNKS_API, siteId, host).replace(
      "<test-device>",
      String(Controller.getInstance().isTestDevice())
    );
    this.sdkEnabledStatusAPI = _makeAPI(SDK_ENABLED_API, siteId, host);
    this.userInfoAPI = _makeAPI(POST_USER_INFO_API, siteId, host);
    this.recordAPI = _makeAPI(RECORD_API, siteId, host);
  }

  private _callback =
    (
      resolve: ResolveCallback = () => null,
      reject: RejectCallback = () => null
    ) =>
    (responseCode: number, response: unknown): void => {
      Logger.debug(`XHR Response: [${responseCode}]`);
      const res = response as Record<string, unknown>;
      if (responseCode < 400) {
        if (
          (res && res[KLARITICS_STATUS_KEY] === KLARITICS_STATUS_SUCCESS) ||
          res[APXOR_STATUS_KEY] === KLARITICS_STATUS_SUCCESS
        ) {
          if (res[DETAILS_KEY]) {
            resolve(res[DETAILS_KEY]);
          } else {
            resolve(res);
          }
        } else if (responseCode === 200) {
          resolve(response);
        } else {
          reject(responseCode);
        }
      } else if (reject && isFunction(reject)) {
        reject(responseCode);
      }
    };

  checkIfSDKEnabled = (
    resolve: ResolveCallback,
    reject: RejectCallback
  ): void => {
    this.makeGetRequest(this.sdkEnabledStatusAPI, resolve, reject);
  };

  getConfig = (resolve: ResolveCallback, reject: RejectCallback): void => {
    this.makeGetRequest(this.configAPI, resolve, reject);
  };

  postUserInfo = (
    data: Record<string, unknown>,
    resolve: ResolveCallback,
    reject?: RejectCallback
  ): void => {
    this.request.post(
      this.userInfoAPI,
      data,
      this._callback(resolve, reject),
      {}
    );
  };

  postChunk = (
    data: Chunk,
    resolve: ResolveCallback,
    reject: RejectCallback,
    sync = true
  ): void => {
    Logger.debug("Posting chunk");
    this.request.post(
      this.chunksAPI,
      data as unknown as Record<string, unknown>,
      this._callback(resolve, reject),
      {},
      sync
    );
  };

  makeGetRequest = (
    url: string,
    resolve: ResolveCallback,
    reject: RejectCallback
  ): void => {
    this.request.get(url, this._callback(resolve, reject));
  };

  makePostRequest = (
    url: string,
    data: Record<string, unknown>,
    headers: Record<string, string>,
    resolve: ResolveCallback,
    reject: RejectCallback
  ): void => {
    this.request.post(url, data, this._callback(resolve, reject), headers);
  };

  /**
   * Transmits compressed session replay data payloads as a binary multi-part stream
   */
  postCompressedVideoChunk = (
    formData,
    resolve: ResolveCallback,
    reject: RejectCallback
  ): void => {
    Logger.debug(`API: Pushing binary replay tracking packet for session`);
    console.log(Object.fromEntries(formData));

    // Sends out multi-part packets completely separate from your metrics endpoint pipeline
    this.request.post(
      this.recordAPI,
      formData as unknown as Record<string, unknown>,
      this._callback(resolve, reject),
      {},
      true
    );
  };
}
